import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, ReactNode } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { GetCruise, GetDay, GetShip, GetShipContext, GetTrip, LogContext } from "../App/Context"
import { computeEditHistory, liveRecord } from "../App/EditHistory"
import { compositeField, FieldFormat } from "../App/Format"
import { AppViewData } from "./App/Context"
import { GetShipUsers } from "./ShipView"
import { value } from "../Components"
import { Block, BlockField, BlockRow } from "../Components/Block"
import { CloseButton } from "../Components/CloseButton"
import { EditHistory } from "../Components/EditHistory"
import { Icon, Icons } from "../Components/Icon"
import { lightingIcon, lightingTranslationKey, lightingValues } from "../Components/LogButton"
import { TabBar } from "../Components/TabBar"
import { UserAdviceRow, userAdviceWidth } from "../Components/UserAdvice/Row"
import { IAppState, IEditPermissivenessLevel, ILog, ILogLighting, IShipStatus, IShipVesselType, IStatusType, Log } from "../Types"
import { LogEnvironmentView, LogEnvironmentViewData } from "./Log/EnvironmentView"
import { LogNavigationView } from "./Log/NavigationView"
import { LogWatchView } from "./Log/WatchView"
import { LogStatusView } from "./Log/StatusView"
import { LogViewProps } from "./Log/Params"
import { showsEditHistory } from "./Log/editability"
import { coveringStatus, engineIdleAtLog, engineRunningAtLog, manualShipStatusActive, shipStatusAtLog, statusesCoveringLog } from "./Log/status"
import { LogCommentText, LogNMEAContext } from "./Log/nmea"
import { ProvisionalCancel, provisionalCancelKey } from "./Log/ProvisionalCancel"

export type { LogViewCreateCallback, LogViewEngineStatusCallback, LogViewLightingCallback, LogViewSailChangesCallback } from "./Log/Params"
export { LogCommentText, LogNMEAInfo, LogNMEAComment } from "./Log/nmea"
export type { LogNMEAContext } from "./Log/nmea"

// Live current value for a `LogEditableField`, used by `computeEditHistory` (issue
// #529) to resolve the "new value" of each field's most recent edit. Most fields map
// 1:1 to a `Log` property; `location` doesn't (it's tracked on the log's own start
// `Status`, not on `Log` itself) and has no live value available here, so its most
// recent edit's `newValue` is left `undefined`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
export function currentLogFieldValue(log: any, field: string): unknown {
  switch (field) {
    case "comment": case "course": case "currentDirection": case "currentSpeed":
    case "depth": case "distance": case "engineRpms": case "heading": case "humidity":
    case "log": case "pressure": case "seaState": case "sog": case "stw":
    case "temperature": case "watch": case "watchComment": case "weather":
    case "windDirection": case "windSpeed":
      return log[field];
    default:
      return undefined;
  }
}

// Per-field value formatters for every Log field (issue #529), mirroring each Log
// sub-view's own `app.format(...)`/predefined-select rendering exactly, so a
// previous/new value shown by `EditHistory` always matches what the (possibly
// now-superseded) input field itself would have shown -- see `Views/Log/
// NavigationView.tsx`/`EnvironmentView.tsx` for the input fields this mirrors. Most
// entries are just a `FormatKind` name (dispatched via `app.format`); `engineRpms`
// (a sparse array) and `currentDirection`/`seaState`/`weather` (predefined-select
// labels) need a `CompositeFormatter` instead, since a bare kind name can't express
// their value shape/translation lookup. `comment`/`location`/`watch`/`watchComment`
// have no entry: they're plain text, already correctly shown as-is by `EditHistory`'s
// generic fallback.
export const LogViewData = {
  fieldFormat(app: IAppState): FieldFormat {
    const ftEnvironment = app.getT("LogEnvironmentView");
    // Mirrors `PredefinedSelect`'s own display rule (`Components/PredefinedSelect.
    // tsx`): a predefined code renders its translated label, anything else (a
    // free-typed custom value) renders as-is.
    const predefined = (prefix: string, codes: string[]) => compositeField(
      (value): value is string | number => typeof value === "string" || typeof value === "number",
      (value): string => {
        const code = String(value);
        return codes.indexOf(code) >= 0 ? ftEnvironment(`${prefix}_${code}`) : code;
      },
    );
    return {
      course: "course",
      currentDirection: predefined("current_direction", LogEnvironmentViewData.predefinedDirections),
      currentSpeed: "speed",
      depth: "meters",
      distance: "distance",
      // Sparse array (unset engines are `-1`, matching `LogNavigationView`'s own
      // `rpmValue`) -- one formatted "N rpm" per set engine, comma-joined.
      engineRpms: compositeField(
        (value): value is number[] => Array.isArray(value),
        (rpms): string => {
          const formatted = rpms.filter((v): v is number => typeof v === "number" && v >= 1).map((v) => app.format("rpm", v));
          return formatted.length > 0 ? formatted.join(", ") : "–";
        },
      ),
      heading: "course",
      humidity: "percentage",
      log: "distance",
      pressure: "pressure",
      seaState: predefined("seastate", LogEnvironmentViewData.predefinedSeaStates),
      sog: "speed",
      stw: "speed",
      temperature: "temperature",
      weather: compositeField(
        (value): value is string | number => typeof value === "string" || typeof value === "number",
        (value): string => ftEnvironment(`weather_${value}`),
      ),
      windDirection: "angle",
      windSpeed: "speed",
    };
  },
  fieldLabel(app: IAppState): (field: string) => string {
    const navigation = app.getT("LogNavigationView");
    const environment = app.getT("LogEnvironmentView");
    const watch = app.getT("LogWatchView");
    const labels: Record<string, string> = {
      comment: navigation("comment"),
      course: navigation("course"),
      depth: navigation("depth"),
      distance: navigation("distance"),
      engineRpms: navigation("engine_rpms"),
      heading: navigation("heading"),
      location: navigation("location"),
      log: navigation("log"),
      sog: navigation("sog"),
      stw: navigation("stw"),
      currentDirection: environment("current_direction_speed"),
      currentSpeed: environment("current_direction_speed"),
      humidity: environment("weather_temp_rel_humidity"),
      pressure: environment("pressure"),
      seaState: environment("seastate"),
      temperature: environment("weather_temp_rel_humidity"),
      weather: environment("weather_field"),
      windDirection: environment("wind_direction_speed"),
      windSpeed: environment("wind_direction_speed"),
      watch: watch("watch"),
      watchComment: watch("watch_comment"),
    };
    return (field: string) => labels[field] ?? field;
  },
};

export function LogView({ advices, adviceState, clearHighlight, createCallback, engineStatusCallback, lightingCallback, sailChangesCallback, initAdvicesExpanded, initEditHistoryOpen, initLogButtonMenuOpen, initLogButtonSubmenu, initSelected, initTab, log, ref, statuses }: LogViewProps) {
  const app = GetApp();
  const { apiUser } = GetShipContext();
  const shipUsers = GetShipUsers();
  const { cruise: _cruise, showLabels } = GetCruise();
  const [ship, trip] = [GetShip(), GetTrip()?.trip];
  const { auditLogEdits, day, focus, isCurrentTrip, logs, statuses: dayStatuses } = GetDay();
  const t = app.getT("LogView");
  const _ft = app.getT("LogView_form");
  const [selected, setSelected] = useState(false);
  const [isMobile, setIsMobile] = useState(window.matchMedia("(max-width: 767px)").matches);
  // The width tier the advice row lays its buttons out for (#1171). Tracked alongside
  // `isMobile` (which only knows the one breakpoint) because the row distinguishes a
  // third, narrower tier where even a warning gives up its wording.
  const [adviceWidth, setAdviceWidth] = useState(() => userAdviceWidth());
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);
  const adviceOverlayRef = useRef<HTMLDivElement>(null);
  const adviceOverlayMobileRef = useRef<HTMLDivElement>(null);

  // Rendered once and shared by the mobile and desktop branches below, so the row's
  // expanded state survives a breakpoint change rather than resetting with it.
  const adviceRow = useMemo(() => {
    if (!advices || !adviceState) {
      return undefined;
    }
    return <UserAdviceRow advices={advices} initExpanded={initAdvicesExpanded} state={adviceState} width={adviceWidth} />;
  }, [advices, adviceState, adviceWidth, initAdvicesExpanded]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    const checkMobile = () => {
      const matches = window.matchMedia("(max-width: 767px)").matches;
      setIsMobile(matches);
      setAdviceWidth(userAdviceWidth());
    };

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      setAdviceWidth(userAdviceWidth());
    };

    const handleResize = () => {
      checkMobile();
    };

    // Listen to matchMedia changes
    mediaQuery.addEventListener("change", handleChange);

    // Listen to window resize (for orientation changes)
    window.addEventListener("resize", handleResize);

    // Listen to orientation changes
    window.addEventListener("orientationchange", checkMobile);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", checkMobile);
    };
  }, []);

  useEffect(() => {
    const updateContainerHeight = () => {
      if (selected && containerRef.current) {
        const isTabletOrBelow = window.matchMedia("(max-width: 1023px)").matches;
        const width = window.innerWidth;
        const isTablet = isTabletOrBelow || width <= 1023;
        if (isTablet) {
          // Wait for content to render, then measure
          setTimeout(() => {
            if (containerRef.current) {
              const height = containerRef.current.scrollHeight;
              setContainerHeight(height);
            }
          }, 100);
        } else {
          setContainerHeight(undefined); // Reset to CSS default for desktop
        }
      }
    };

    updateContainerHeight();

    // Listen to window resize and orientation changes
    window.addEventListener("resize", updateContainerHeight);
    window.addEventListener("orientationchange", updateContainerHeight);

    return () => {
      window.removeEventListener("resize", updateContainerHeight);
      window.removeEventListener("orientationchange", updateContainerHeight);
    };
  }, [selected]);

  const _calculated = log.calculated;
  const metadata = useMemo(() => Log.parse(log).dataParsed, [log]);
  // Engine-missing ghosts copy a committed departure log but use id 0 to stay outside
  // the committed timeline. Resolve their status chrome against that departure rather
  // than treating them like the live "now" entry.
  const statusLogId = metadata.engineMissing?.sinceLogId ?? log.id;
  // A provisional (in-progress, uncommitted) auto-log is flagged in its metadata. It is
  // NOT the live current entry — it carries none of the current log's chrome (no + Log
  // button, advices or `LogView-current-entry` styling). Instead it gets a shared
  // `LogView-provisional` class and a per-type `LogView-provisional-<type>` class so each
  // kind of provisional entry (course change, …) can be styled distinctly.
  //
  // The live current entry is also broadcast flagged `nmeaAutoPending` (it too is an
  // uncommitted entry), but it keeps its own `LogView-current-entry` chrome and must NOT
  // pick up the provisional styling, so exclude it here.
  const provisional = (metadata.nmeaAutoPending || false) && !(log.nmeaPending || false);
  const provisionalClass = provisional ? ` LogView-provisional LogView-provisional-${metadata.type}` : "";
  const focusEditable = focus && AppViewData.availableCurrentOrAll(apiUser, ship.id, isCurrentTrip);
  // A cancellable provisional entry (#872) offers a cancel button, letting the user drop
  // both the live entry and the navigation state that produced it. Gated on the same
  // edit rights as the current log's advices — deliberately NOT on `editable` below,
  // which requires a committed (`id > 0`) log a provisional entry by definition isn't.
  const cancelKey = useMemo(() => (provisional ? provisionalCancelKey(metadata) : undefined), [provisional, metadata]);
  const provisionalCancel = cancelKey != undefined && focusEditable && app.editable
    ? <div className="LogView-provisional-cancel"><ProvisionalCancel cancelKey={cancelKey} kind={metadata.type} /></div>
    : undefined;
  // Whether *any* field on this log could conceivably be edited here at all — i.e. the
  // user has ship-level edit rights, this is the focused tab, and the log is a real
  // (committed) entry. This deliberately does NOT gate on the log's age/recency or
  // whether it's still the most recent of its kind: those restrictions only apply to
  // `.nmeaCapable` fields (issue #529) and are enforced per-field, in each editable
  // field's own `readOnly` via `Views/Log/editability.ts`'s `logFieldEditable` — an
  // `.always` field (e.g. comment) must stay editable at any age. Superseded the old
  // `lastLog`/`app.forceLogEditable`/1-hour heuristic, which used to gate the whole
  // form (and so incorrectly blocked `.always` fields too) — see `logFieldEditable`.
  const editable = useMemo(() => (app.editable && log.id > 0 && focus && focusEditable) || false, [log, focus, focusEditable]);

  // Measure advice overlay height and adjust parent row height. Uses
  // `useLayoutEffect` (synchronous, after DOM mutations but before paint)
  // rather than `useEffect` + `setTimeout(fn, 0)`: the previous deferred
  // measurement raced the real macrotask queue, so whether it had run by the
  // time anything (a test, or just a user) observed the DOM was
  // non-deterministic -- causing both a visible flash of the unmeasured
  // 49px row in the browser and flaky golden snapshots in CI. Re-measuring is
  // driven by a `ResizeObserver` on the overlay itself (mirrors `PrintHost.tsx`
  // and `useOverlayScroll.ts`) rather than a `window` resize listener, so it
  // also reacts to the overlay's own content reflowing (e.g. advice text
  // wrapping differently after a font swap) and not just the window resizing.
  useLayoutEffect(() => {
    // `app.staticLayout` skips this real-browser measurement in the catalog +
    // unit-test (happy-dom) mock app so the golden snapshots stay deterministic
    // (see `IAppState.staticLayout`). Real users + E2E run it.
    if (app.staticLayout || !advices || !createCallback || !focusEditable) return;
    const overlay = isMobile ? adviceOverlayMobileRef.current : adviceOverlayRef.current;
    if (!overlay) return;

    const measureOverlay = () => {
      const height = overlay.offsetHeight;
      const parent = overlay.parentElement;
      if (!parent) return;
      if (height > 49) {
        // The overlay is absolutely positioned, so it ignores the row's own
        // padding-bottom (the 4px `.LogView-current-entry` reserves below it at
        // mobile widths, LogView.scss) -- once the overlay wraps past one line
        // that gap has to be added back here, or the last button row ends up
        // flush with the row's bottom edge instead of matching the single-line gap.
        parent.style.minHeight = `${height + (isMobile ? 4 : 0)}px`;
      } else {
        parent.style.minHeight = "49px";
      }
    };

    measureOverlay();

    const resizeObserver = new ResizeObserver(measureOverlay);
    resizeObserver.observe(overlay);
    return () => resizeObserver.disconnect();
  }, [advices, createCallback, focusEditable, isMobile]);

  const engineStatuses = useMemo(() => {
    return statuses.filter((s) => s.statusType == IStatusType.engine);
  }, [statuses, log]);
  const sailStatuses = useMemo(() => {
    return statuses.filter((s) => s.statusType == IStatusType.sails);
  }, [statuses, log]);

  const engines = useMemo(() => {
    const engineInfo = [];
    const thresholds = Ship.shipSettings(ship).thresholds;
    for (const e of Ship.engines(ship)) {
      const n = value(-1, e.nmeaNumber, e.number);
      // The covering per-engine status (started at or before this log, not yet closed
      // at it) carries the persisted run state (#619): running vs idle.
      const status = coveringStatus(engineStatuses, statusLogId, e.id);
      const on = engineRunningAtLog(log.engineRpms, n, status, thresholds);
      const idle = on && engineIdleAtLog(log.engineRpms, n, status, thresholds);
      engineInfo.push({ id: e.id, number: e.number, name: e.engineShortName, on: on, idle: idle });
    }
    return engineInfo;
  }, [engineStatuses, ship, log, statusLogId]);

  const sails = useMemo(() => {
    const sailInfo = [];
    for (const s of Ship.sails(ship)) {
      const status = coveringStatus(sailStatuses, statusLogId, s.id);
      const active = status != undefined;
      sailInfo.push({ id: s.id, name: s.sail, active: active, reef: status?.value || 0 });
    }
    return sailInfo;
  }, [sailStatuses, ship, statusLogId]);

  // Sails offered to the quick sail-plan toggle in the LogButton overlay. Only a
  // sailing vessel with configured sails gets the block; the title falls back to a
  // predefined-sail translation, then the raw custom name.
  const sailButtons = useMemo(() => {
    if (ship.vesselType != IShipVesselType.sailing) {
      return [];
    }
    const st = app.getT("ShipDataView");
    return Ship.sails(ship).map((s) => {
      const active = coveringStatus(sailStatuses, statusLogId, s.id) != undefined;
      const title = s.sail ? (st(`sail_${s.sail}`) || s.sail) : s.id;
      return { id: s.id, name: s.sail, sailType: s.sailType, title, active };
    });
  }, [sailStatuses, ship, statusLogId, app.status]);

  const shipStatus = useMemo(() => {
    return shipStatusAtLog(statuses, statusLogId);
  }, [statusLogId, statuses]);

  // The manual ship-status mode (#511) in force at this log, so the LogButton's
  // manual-status action can render as on and switch it off again.
  const manualStatusActive = useMemo(
    () => manualShipStatusActive(statuses, statusLogId),
    [statuses, statusLogId],
  );

  const lightingStatus = useMemo(() => {
    // The lighting in force at *this* log, not whatever is currently switched on: for
    // an older entry the open status may well have started after it (#506).
    const lightingStatuses = statusesCoveringLog(statuses.filter((s) => s.statusType == IStatusType.lighting), statusLogId);
    return lightingStatuses[lightingStatuses.length - 1];
  }, [statuses, statusLogId]);
  useEffect(() => {
    if (initSelected && !selected) {
      setSelected(true);
      clearHighlight();
    }
  }, [initSelected, selected]);

  const _debugData = useMemo((): string => {
    return ["logId:", log.id, "dayId:", log.dayId, "tripId:", log.tripId, "cruiseId:", log.cruiseId].join(" ");
  }, [log]);

  const comment = useMemo((): string => {
    const ctx: LogNMEAContext = { app, ship, trip, shipUsers, logs, dayStatuses, day, auditLogEdits };
    return LogCommentText(log, statuses, ctx);
  }, [log, log.comment, statuses, app, ship, trip, shipUsers, logs, dayStatuses, day, auditLogEdits]);

  const labels = useMemo((): ReactNode => {
    if (!log.labels || log.labels.length < 1 || !showLabels) {
      return null;
    }
    return (
      <span className="LogView-labels">
        {log.labels.map((label) => {
          return <span key={`log-label-${label}`} className="LogView-label">{t(`label_${label}`)}</span>;
        })}
      </span>
    );
  }, [log]);

  function toggleSelected() {
    setSelected(!selected);
  }
  const shipStatusValue = useMemo(() => {
    if (shipStatus == undefined) {
      return IShipStatus.none;
    }
    return shipStatus.value as IShipStatus;
  }, [shipStatus, shipStatus?.value]);

  const sailStatusIcon = useMemo(() => {
    const activeSails = sails.filter((s) => s.active);
    if (activeSails.length > 0) {
      return <Icon className="LogView-status-icon" icon={Icons.SailingVesselLarge} />;
    }
    return null;
  }, [sails]);

  const engineStatusIcons = useMemo(() => {
    // Always render icons only — engine toggle buttons have moved to the LogButton overlay.
    // off = outline circle, running = filled circle, idle = dot-in-ring (#619).
    return engines.map((e) => {
      const state = e.on ? (e.idle ? "idle" : "on") : "off";
      const icon = !e.on ? Icons.CircleLarge : (e.idle ? Icons.CircleHalfLarge : Icons.CircleFilledLarge);
      return <Icon key={`engine-${e.id}`} className={`LogView-status-icon engine-${state}`} icon={icon} title={app.t(`engine_${e.name}`)} />;
    });
  }, [engines, app.status]);

  const commentStatusIcons = useMemo(() => {
    return (
      <span className="LogView-status-icons">
        {sailStatusIcon && <span className="sail-icon">{sailStatusIcon}</span>}
        <span className="engine-icons">
          {engineStatusIcons}
        </span>
      </span>
    );
  }, [sailStatusIcon, engineStatusIcons]);

  const logCreator = useMemo(() => {
    return shipUsers?.find((u) => u.id == log.userId);
  }, [log.userId, shipUsers]);

  // Edit history (issue #529): this log's own `AuditLogEdit` rows, matched via
  // `logId` out of `DayContext.auditLogEdits` (which carries every log's edits for
  // the day). Sorted most-recent-first by `computeEditHistory`, so its first entry
  // is also the log's last edit overall, driving the `.log-details` "last edited by"
  // attribution below.
  const logAuditLogEdits = useMemo(() => {
    return auditLogEdits.filter((e) => e.logId == log.id);
  }, [auditLogEdits, log.id]);
  // Issue #1136: this log as last returned by one of the edit sub-views' own saves.
  // `log` itself comes from the day's `logs` list and only refreshes on the next live
  // broadcast / full day load, so without this the field just saved keeps resolving
  // its "new value" below from the pre-edit snapshot. Handed down to the sub-views
  // (the save happens there, the edit history renders here) and only ever read for
  // that resolution -- their own inputs keep what the user typed.
  const [savedLog, setSavedLog] = useState<ILog | undefined>(undefined);
  const recordSavedLog = useCallback((saved: ILog) => {
    setSavedLog(saved);
  }, []);
  const editHistory = useMemo(() => {
    const historyLog = liveRecord(log, savedLog);
    return computeEditHistory(logAuditLogEdits, (field) => currentLogFieldValue(historyLog, field));
  }, [logAuditLogEdits, log, savedLog]);
  const lastEditor = useMemo(() => {
    if (editHistory.length < 1) {
      return undefined;
    }
    return shipUsers?.find((u) => u.id == editHistory[0].shipUserId);
  }, [editHistory, shipUsers]);
  // Edit History visibility (issue #1169): a code-level policy, not a ship
  // setting -- `.unrestricted` already discards the field-level protections that
  // give the audit trail its evidentiary value, so the table (but not the "last
  // edited" line itself) is hidden at that level. See `Log/editability.ts`.
  const editPermissivenessLevel = Ship.shipSettings(ship).editPermissiveness ?? IEditPermissivenessLevel.strict;
  // Existing audit history remains visible even when new log editing is disabled.
  const editHistoryVisible = showsEditHistory(editPermissivenessLevel);
  const [editHistoryOpen, setEditHistoryOpen] = useState(initEditHistoryOpen ?? false);
  const editHistoryFieldFormat = useMemo(() => LogViewData.fieldFormat(app), [app]);
  const fieldLabel = useMemo(() => LogViewData.fieldLabel(app), [app]);

  const statusOptions = useMemo(() => Object.entries(IShipStatus).filter((s) => isNaN(parseInt(s[0]))).map((s) => {
    const [status, i] = s;
    if (status == "none" || (shipStatus && shipStatus.value == i)) {
      return undefined;
    }
    return { icon: `status-${status}`, title: app.t(`LogNavigationView_status_${status}`), value: i };
  }).filter((n) => n != undefined), [log, shipStatus, statuses]);

  const lightingValue = useMemo(() => {
    return (lightingStatus?.value ?? ILogLighting.none) as ILogLighting;
  }, [lightingStatus, lightingStatus?.value]);

  const lightingOptions = useMemo(() => lightingValues(ship.vesselType)
    .filter((value) => value != lightingValue)
    .map((value) => ({
      icon: lightingIcon(value),
      title: app.t(lightingTranslationKey(value, ship.vesselType)),
      value,
    })), [lightingValue, ship.vesselType, app, app.language]);

  const hour = Math.floor(log.time / 3600);

  return (
    <LogContext.Provider value={log}>
      <LogStatusView
        createCallback={createCallback}
        focusEditable={focusEditable || false}
        engines={engines.map((e) => ({ id: e.id, name: e.name || "", on: e.on, idle: e.idle }))}
        hasNmeaEngineData={(app.status?.nmea?.data.engine?.length || 0) > 0}
        engineStatusCallback={engineStatusCallback}
        initLogButtonMenuOpen={initLogButtonMenuOpen}
        initLogButtonSubmenu={initLogButtonSubmenu}
        lightingCallback={lightingCallback}
        lightingValue={lightingValue}
        lightingOptions={lightingOptions}
        sails={sailButtons}
        sailChangesCallback={sailChangesCallback}
        label={t("nmea_pending")}
        manualStatusActive={manualStatusActive}
        shipStatusValue={shipStatusValue}
        statusOptions={statusOptions}
      />
      <div className="DayView-logs-timeline log"><div className={"hour hour-" + hour}></div><div className="fill"></div></div>
      <div className={"LogView " + (selected ? "selected" : "not-selected") + (log.nmeaPending ? " LogView-current-entry" : "") + provisionalClass}>
        {ref && <div className="LogView-ref-parent"><div className="LogView-ref-placeholder" ref={ref}></div></div>}

        {!selected && isMobile ? (
          <div className="LogView-header LogView-header-mobile" onClick={toggleSelected}>
            <div className="LogView-header-row-1">
              <div className="LogView-cell LogView-time">{app.format("time", log.time)}</div>
              <div className="LogView-cell LogView-course">{app.format("course", log.course)}</div>
              <div className="LogView-cell LogView-speed">{app.format("speed", log.sog)}</div>
              <div className="LogView-cell LogView-distance">{app.format("distance", log.distance)}</div>
            </div>
            <div className="LogView-header-row-2">
              {commentStatusIcons}
              {advices && createCallback && focusEditable ? (
                <div className="LogView-comment-text"></div>
              ) : (
                <div className="LogView-comment-text">
                  {labels}{comment}
                </div>
              )}
            </div>
            {advices && createCallback && focusEditable && (
              <div className="LogView-advice-overlay-mobile" ref={adviceOverlayMobileRef}>
                {adviceRow}
              </div>
            )}
            {provisionalCancel}
          </div>
        ) : (
          <div className="LogView-header LogView-header-normal">
            <div className="LogView-cell LogView-time" onClick={toggleSelected}>{app.format("time", log.time)}</div>
            {advices && createCallback && focusEditable ? (
              <>
                <div className="LogView-cell LogView-comment" onClick={toggleSelected}>
                  <div className="LogView-comment-content"></div>
                  {commentStatusIcons}
                </div>
                <div className="LogView-advice-overlay" ref={adviceOverlayRef}>
                  {adviceRow}
                </div>
              </>
            ) : (
              <div className="LogView-cell LogView-comment" onClick={toggleSelected}>
                <div className="LogView-comment-content">{labels}{comment}</div>
                {commentStatusIcons}
                {/* <span className="debug-data"> {debugData}</span> */}
              </div>
            )}
            <div className="LogView-cell LogView-course">{app.format("course", log.course)}</div>
            <div className="LogView-cell LogView-speed">{app.format("speed", log.sog)}</div>
            <div className="LogView-cell LogView-distance">{app.format("distance", log.distance)}</div>
            <div className="LogView-gap">
              {provisionalCancel}
              {selected && (
                <div className="LogView-container-close">
                  <CloseButton icon={editable ? Icons.Enter : undefined} onClick={toggleSelected} />
                </div>
              )}
            </div>
          </div>
        )}
        {selected && (
          <>
            <div
              ref={containerRef}
              className={
                shipStatus &&
                shipStatus.value !== IShipStatus.inmotion &&
                shipStatus.value !== IShipStatus.drifting
                  ? "LogView-container LogView-container-status-stopped"
                  : "LogView-container"
              }
              style={containerHeight ? { height: `${containerHeight}px` } : undefined}
            >
              <TabBar
                defaultSelection={initTab || "navigation"}
                tabs={[
                  {
                    icon: Icons.Navigation,
                    name: "navigation",
                    title: t("tab_navigation"),
                    render: () => <LogNavigationView editable={editable} recordSavedLog={recordSavedLog} status={shipStatus} statuses={statuses} updated={log.updatedAt} />,
                  },
                  {
                    icon: Icons.Environment,
                    name: "environment",
                    title: t("tab_environment"),
                    render: () => <LogEnvironmentView editable={editable} recordSavedLog={recordSavedLog} />,
                  },
                  {
                    icon: Icons.Watch,
                    name: "watch",
                    title: t("tab_watch"),
                    render: () => <LogWatchView editable={editable} recordSavedLog={recordSavedLog} />,
                  },
                ]}
              />
              <Block className="log-details">
                <BlockRow>
                  {logCreator && (
                    <BlockField>{t("details_created_by", { name: logCreator.name || logCreator.shortname || "" })}</BlockField>
                  )}
                  {log.updatedAt != log.createdAt && (
                    <BlockField>{t("details_updated_at", { date: app.format("dateAndTime", log.updatedAt) })}</BlockField>
                  )}
                  {editHistory.length > 0 && (
                    <BlockField
                      className="log-details-last-edited"
                      onClick={editHistoryVisible ? () => setEditHistoryOpen((open) => !open) : undefined}
                    >
                      {lastEditor
                        ? t("details_last_edited_by", { name: lastEditor.name || lastEditor.shortname || "" })
                        : t("details_last_edited")}
                      {editHistoryVisible && <Icon icon={editHistoryOpen ? Icons.ArrowUp : Icons.ArrowDown} />}
                    </BlockField>
                  )}
                </BlockRow>
              </Block>
              {editHistoryVisible && (
                <EditHistory entries={editHistory} fieldLabel={fieldLabel} fieldFormat={editHistoryFieldFormat} open={editHistoryOpen} />
              )}
            </div>
          </>
        )}
      </div>
    </LogContext.Provider>
  );
}
